import type { TaskSource, Task } from '../lib/api'

const SOURCE_LABELS: Record<TaskSource, string> = {
  dify: 'Dify',
  opencode: 'OpenCode',
  supercoldbuilder: 'SuperColdBuilder',
  manual: 'Manuel',
}

const SOURCE_STYLES: Record<TaskSource, string> = {
  dify: 'bg-blue-100 text-blue-700',
  opencode: 'bg-purple-100 text-purple-700',
  supercoldbuilder: 'bg-orange-100 text-orange-700',
  manual: 'bg-gray-100 text-gray-600',
}

function SourceBadge({ source, sessionLink }: { source: TaskSource; sessionLink?: string | null }) {
  const label = SOURCE_LABELS[source] ?? source
  const cls = SOURCE_STYLES[source] ?? 'bg-gray-100 text-gray-600'
  const inner = (
    <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${cls}`}>
      {label}
    </span>
  )
  if (sessionLink) {
    return (
      <a href={sessionLink} target="_blank" rel="noopener noreferrer" title="Ouvrir la session" className="hover:underline">
        {inner}
      </a>
    )
  }
  return inner
}

interface TaskCardProps {
  task: Task
  isDragging: boolean
  onDragStart: (id: number) => void
  onDelete: (id: number) => void
  onDragOver: () => void
}

export default function TaskCard({ task, isDragging, onDragStart, onDelete, onDragOver }: TaskCardProps) {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', String(task.id))
    onDragStart(task.id)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onDragOver()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  return (
    <div
      draggable
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`group bg-white rounded-lg shadow-sm border border-kanban-border p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition ${
        isDragging ? 'opacity-40' : ''
      }`}
    >
      <p className="text-sm font-medium text-text-primary break-words leading-snug">{task.title}</p>
      {task.description && (
        <p className="text-xs text-text-soft mt-1 break-words">{task.description}</p>
      )}
      <div className="flex items-center gap-2 mt-2">
        {task.source && <SourceBadge source={task.source} sessionLink={task.session_link} />}
        <button
          onClick={() => onDelete(task.id)}
          className="ml-auto text-text-muted hover:text-red-600 opacity-0 group-hover:opacity-100 transition"
          title="Supprimer la tâche"
          aria-label="Supprimer la tâche"
        >
          ×
        </button>
      </div>
    </div>
  )
}
